import { z, type ZodError } from "zod";

export const minimumUserPasswordLength = 8;

export const createUserSchema = z
  .object({
    name: z.string().trim().min(1),
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(minimumUserPasswordLength),
    role: z.enum(["admin", "staff"]).default("staff"),
    accountType: z.enum(["internal", "external"]).default("internal"),
    jobTitle: z.string().trim().min(1).default("Staff"),
  })
  .refine((value) => value.role !== "admin" || value.accountType === "internal", { path: ["accountType"] });

export const updateUserSchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    role: z.enum(["admin", "staff"]).optional(),
    accountType: z.enum(["internal", "external"]).optional(),
    jobTitle: z.string().trim().min(1).optional(),
    status: z.enum(["active", "disabled"]).optional(),
    password: z.string().min(minimumUserPasswordLength).optional(),
  })
  .strict()
  .refine((value) => value.role !== "admin" || value.accountType !== "external", { path: ["accountType"] });

export type CreateUserInput = z.infer<typeof createUserSchema>;

export type CreateUserValidationCode =
  | "name_required"
  | "email_invalid"
  | "password_too_short"
  | "job_title_required"
  | "role_invalid"
  | "account_type_invalid"
  | "status_invalid"
  | "invalid_payload";

export function getCreateUserValidationCode(error: ZodError): CreateUserValidationCode {
  const field = error.issues[0]?.path[0];

  if (field === "name") return "name_required";
  if (field === "email") return "email_invalid";
  if (field === "password") return "password_too_short";
  if (field === "jobTitle") return "job_title_required";
  if (field === "role") return "role_invalid";
  if (field === "accountType") return "account_type_invalid";
  if (field === "status") return "status_invalid";
  return "invalid_payload";
}

export function getCreateUserValidationMessage(code: CreateUserValidationCode): string {
  const messages: Record<CreateUserValidationCode, string> = {
    name_required: "Name is required",
    email_invalid: "A valid email address is required",
    password_too_short: `Password must be at least ${minimumUserPasswordLength} characters`,
    job_title_required: "Job title is required",
    role_invalid: "Role must be admin or staff",
    account_type_invalid: "Account type must be internal or external",
    status_invalid: "Status must be active or disabled",
    invalid_payload: "Invalid user payload",
  };

  return messages[code];
}
